// Central content for the SwastSaathi website.
// Add or edit services, partner types, phases and locations here — pages read from this file.
import {
  MapPin, Puzzle, Clock, Wallet, Repeat, Stethoscope, FlaskConical, Pill, HeartPulse,
  Activity, Hospital, FileHeart, Users, ShieldCheck, Cpu, Handshake, Scale, HeartHandshake,
  Building2, School, Sprout, Eye, Network, Smartphone, LineChart, ClipboardCheck,
} from "lucide-react";

export const SITE = {
  name: "SwastSaathi",
  domain: "swastsaathi.in",
  url: "https://swastsaathi.in",
  tagline: "Healthcare, Closer to Home.",
  founder: "Aman Kumar",
  region: "Bihar, India",
  stage: "Idea-stage venture",
};

const u = (id, w = 1600) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=72`;
const px = (id, w = 1400) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

export const IMAGES = {
  hero: u("photo-1609252509229-364936a1d1a2", 1800),
  elder: u("photo-1632414237690-7713a79fe9d3", 1200),
  village: u("photo-1586618770443-e6f8167fca61", 1400),
  phoneGroup: u("photo-1566132020005-e42ced040a92", 1400),
  phoneWoman: u("photo-1645538165779-81d7f49baa6f", 1200),
  grandmother: u("photo-1724301964759-374723c8ee7d", 1200),
  fieldWoman: u("photo-1707811179851-c1f93698ad46", 1400),
  aerial: u("photo-1792317751944-3c2826fee9c3", 1800),
  pharmacy: u("photo-1611072965169-e1534f6f300c", 1000),
  doctorSenior: px("18870282", 1200),
  lab: px("3735705", 1000),
  doctorPhone: px("8413399", 1000),
  doctorIndianMale: u("photo-1642975967602-653d378f3b5b", 1000),
  telePartners: "/telemedicine-partners.webp",
  teleInvestors: "/telemedicine-investors.webp",
  biharMorning: px("34442874", 1800),
};

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/healthcare-model", label: "Healthcare Model" },
  { to: "/vision-impact", label: "Vision & Impact" },
  { to: "/partners", label: "Partners" },
  { to: "/investors", label: "Investors" },
  { to: "/contact", label: "Contact" },
];

export const PROBLEMS = [
  { icon: MapPin, title: "Distance from qualified care", body: "Qualified healthcare professionals are often located far from villages, so even a routine consultation can mean a long journey." },
  { icon: Puzzle, title: "Fragmented access", body: "Consultations, diagnostic tests and medicines are frequently found in different places, leaving patients to coordinate each step on their own." },
  { icon: Clock, title: "Delayed treatment, limited follow-up", body: "When care is hard to reach, people tend to wait until symptoms worsen, and follow-up visits are easily missed." },
  { icon: Wallet, title: "Travel costs and lost wages", body: "Every trip to a distant facility can bring travel expenses and a lost day of work for the patient and a family member." },
  { icon: Repeat, title: "Weak continuity for chronic conditions", body: "Conditions that need regular monitoring are difficult to manage without a consistent, nearby point of contact." },
];

export const JOURNEY = [
  { title: "Visit a community access point", body: "The patient visits a nearby SwastSaathi community healthcare access point." },
  { title: "Registration & basic screening", body: "A trained healthcare worker helps with registration and basic, non-diagnostic screening." },
  { title: "Connect with a licensed doctor", body: "The patient is connected with an appropriately licensed doctor, in person or through teleconsultation." },
  { title: "Diagnostics & medicines coordinated", body: "Investigations and prescribed medicines are coordinated through authorised diagnostic and pharmacy partners." },
  { title: "Follow-up & referral", body: "Follow-up, preventive care and referrals to hospitals are coordinated where appropriate." },
];

export const SERVICES = [
  { icon: Stethoscope, title: "Doctor consultations & telemedicine coordination", body: "Helping patients reach appropriately licensed doctors, with clinical decisions always made by the doctor.", span: "lg:col-span-2" },
  { icon: FlaskConical, title: "Diagnostic laboratory partnerships", body: "Coordinating sample collection and reports through accredited, authorised laboratory partners." },
  { icon: Pill, title: "Pharmacy coordination", body: "Connecting prescribed medicines to licensed pharmacy partners closer to the community." },
  { icon: Sprout, title: "Preventive healthcare & health education", body: "Community awareness sessions and preventive check-ups planned with qualified professionals." },
  { icon: HeartPulse, title: "Chronic disease follow-up", body: "Reminders and regular follow-up support for people living with long-term conditions." },
  { icon: Hospital, title: "Referral support", body: "Helping patients reach the right hospital or specialist when a higher level of care is needed." },
  { icon: FileHeart, title: "Digital health records & care coordination", body: "Consent-based digital records to keep care connected across providers, built with data protection in mind.", span: "md:col-span-2 lg:col-span-2" },
];

export const REVENUE = [
  { title: "Consultation coordination fees", body: "Modest fees for organising and supporting consultations with licensed doctors." },
  { title: "Diagnostic service agreements", body: "Service arrangements with authorised diagnostic laboratories for coordinated testing." },
  { title: "Family healthcare memberships", body: "Affordable household plans that bundle coordination and preventive services." },
  { title: "Preventive health packages", body: "Periodic check-up packages designed with qualified healthcare partners." },
  { title: "Institutional contracts", body: "Programmes for NGOs, employers, schools and public-health initiatives." },
  { title: "Future technology & managed-partner services", body: "Coordination software and managed services for partner centres over time." },
];

export const OUTCOMES = [
  { icon: MapPin, title: "Better access to primary healthcare" },
  { icon: Wallet, title: "Reduced avoidable travel and inconvenience" },
  { icon: Eye, title: "Earlier identification of health risks" },
  { icon: Repeat, title: "Improved continuity of care" },
  { icon: Network, title: "Stronger links between communities and providers" },
];

export const PRINCIPLES = [
  { icon: MapPin, title: "Accessibility", body: "Bring the first point of care closer to where people live and work." },
  { icon: Wallet, title: "Affordability", body: "Design services and pricing around what rural households can sustain." },
  { icon: ShieldCheck, title: "Trust", body: "Be transparent about what we do, what we do not do, and who provides care." },
  { icon: Cpu, title: "Technology", body: "Use simple, reliable digital tools that support people rather than replace them." },
  { icon: Handshake, title: "Partnerships", body: "Strengthen existing providers and infrastructure instead of duplicating them." },
  { icon: Scale, title: "Clinical responsibility", body: "Clinical decisions stay with appropriately licensed and qualified professionals." },
];

export const ECOSYSTEM = [
  { key: "patients", label: "Patients", note: "Families in rural communities", icon: Users },
  { key: "centre", label: "Community Healthcare Centre", note: "Access & coordination point", icon: HeartHandshake, hub: true },
  { key: "doctors", label: "Licensed Doctors", note: "Clinical decisions", icon: Stethoscope, clinical: true },
  { key: "diagnostics", label: "Diagnostic Partners", note: "Authorised laboratories", icon: FlaskConical, clinical: true },
  { key: "pharmacy", label: "Pharmacy Partners", note: "Licensed pharmacies", icon: Pill, clinical: true },
  { key: "hospitals", label: "Referral Hospitals", note: "Higher levels of care", icon: Hospital, clinical: true },
];

export const MODEL_SECTIONS = [
  { id: "community", icon: Users, title: "Community engagement", body: "Centres are intended to be rooted in the community — working with local leaders, self-help groups and families so that people know the access point, trust it and use it early rather than late." },
  { id: "preventive", icon: Sprout, title: "Preventive healthcare", body: "Planned health-awareness sessions and periodic check-ups, designed with qualified professionals, aim to help families recognise risks earlier." },
  { id: "chronic", icon: HeartPulse, title: "Chronic disease follow-up", body: "For people living with long-term conditions, the model proposes reminders, regular check-ins and coordinated review by their doctor." },
  { id: "providers", icon: Handshake, title: "Provider partnerships", body: "SwastSaathi intends to work with licensed doctors, authorised laboratories, licensed pharmacies and hospitals under clear agreements, rather than delivering clinical services itself." },
  { id: "quality", icon: ClipboardCheck, title: "Quality assurance", body: "Partner verification, standard operating procedures, worker training and periodic review are planned so that quality can be monitored consistently across centres." },
  { id: "digital", icon: Smartphone, title: "Digital coordination", body: "Simple digital tools are planned to manage registrations, appointments, referrals and follow-ups, keeping each patient's care connected." },
  { id: "responsible", icon: ShieldCheck, title: "Responsible use of technology", body: "Technology supports — never replaces — clinical judgement. Any future health data would be handled with consent, minimal collection and applicable Indian data-protection requirements." },
];

export const ROADMAP = [
  { phase: "Phase 1", title: "Validate & pilot in Bihar", body: "Validate the concept with communities and providers, and conduct a focused pilot in Bihar." },
  { phase: "Phase 2", title: "Refine the operating model", body: "Refine operations from pilot learnings and work towards demonstrating sustainable unit economics." },
  { phase: "Phase 3", title: "Expand across districts", body: "Expand to additional districts only after successful validation of the model." },
  { phase: "Phase 4", title: "New states & markets", body: "Explore neighbouring states and additional underserved markets." },
];

export const INDICATORS = [
  { icon: Users, title: "Patients served", body: "How many people use a centre and the services coordinated through it." },
  { icon: Repeat, title: "Repeat visits", body: "Whether patients return for follow-up and ongoing care." },
  { icon: HeartHandshake, title: "Patient satisfaction", body: "How patients and families rate their experience and trust." },
  { icon: Hospital, title: "Referral completion", body: "Whether referred patients actually reach the next level of care." },
  { icon: Wallet, title: "Affordability", body: "Out-of-pocket and travel costs compared with patients' previous options." },
  { icon: LineChart, title: "Centre-level sustainability", body: "Whether each centre can cover its operating costs over time." },
];

export const PARTNER_TYPES = [
  { icon: Stethoscope, title: "Licensed doctors", body: "Offer consultations — in person or via teleconsultation — to patients coordinated through community access points." },
  { icon: FlaskConical, title: "Diagnostic laboratories", body: "Extend your reach through coordinated sample collection and reporting for rural patients." },
  { icon: Pill, title: "Licensed pharmacies", body: "Help ensure prescribed medicines reach patients reliably and closer to home." },
  { icon: Hospital, title: "Hospitals & referral providers", body: "Receive appropriately referred patients with better context and coordinated follow-up." },
  { icon: HeartHandshake, title: "NGOs & community organisations", body: "Collaborate on outreach, health education and community trust-building." },
  { icon: School, title: "Schools, employers & institutions", body: "Explore preventive health programmes for students, staff and members." },
];

export const PARTNER_BENEFITS = [
  "Reach patients in underserved communities without building new infrastructure",
  "Receive organised, pre-registered patients through a trusted local access point",
  "Clear roles: clinical decisions remain with you as the licensed professional",
  "Shared digital coordination for appointments, referrals and follow-ups",
  "Contribute to measurable, community-level health outcomes over time",
];

export const PARTNER_INTERESTS = [
  "Licensed doctor / teleconsultation", "Diagnostic laboratory", "Licensed pharmacy",
  "Hospital / referral provider", "NGO / community organisation", "School, employer or institution", "Other",
];

export const SEGMENTS = [
  { title: "Rural households", body: "Families in villages and small towns seeking nearby, affordable primary care." },
  { title: "People with chronic conditions", body: "Patients who need regular monitoring and follow-up close to home." },
  { title: "Healthcare providers", body: "Doctors, labs, pharmacies and hospitals looking to reach underserved patients." },
  { title: "Institutions", body: "NGOs, employers, schools and public-health programmes seeking delivery partners." },
];

export const MILESTONES = [
  "Community discovery: interviews with households and local stakeholders in the pilot area",
  "Provider interest: conversations with doctors, labs and pharmacies on participation terms",
  "Regulatory review: confirm compliance requirements for each coordinated service",
  "Pilot design: site selection, worker training plan and operating procedures",
  "Pricing tests: willingness-to-pay for coordination fees and memberships",
  "Unit economics: evidence on whether a single centre can become sustainable",
];

export const RISKS = [
  { risk: "Low initial community trust or uptake", mitigation: "Community-led outreach, local workers and transparency about who provides care." },
  { risk: "Limited provider participation", mitigation: "Simple partnership terms, organised patient flow and clear clinical roles." },
  { risk: "Regulatory and compliance complexity", mitigation: "Partnership-led model, licensed providers only, and legal review before launch." },
  { risk: "Unproven centre-level unit economics", mitigation: "Small focused pilot, cost discipline and multiple validated revenue streams." },
  { risk: "Connectivity and technology constraints", mitigation: "Low-bandwidth, assisted digital tools with offline-tolerant workflows." },
  { risk: "Quality consistency across centres", mitigation: "Standard procedures, worker training, partner verification and periodic review." },
];

export const INVESTOR_INTERESTS = [
  "Early-stage investment", "Incubation / acceleration", "Startup competition", "Mentorship", "Grant or impact funding", "Other",
];

export const CONTACT_TYPES = [
  { value: "general", label: "General enquiry" },
  { value: "partnership", label: "Partnership enquiry" },
  { value: "investment", label: "Investor & incubation enquiry" },
];
